import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldCheck, ShieldWarning, Shield } from "@phosphor-icons/react";

function Card({ label, value, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-white border-slate-200",
    good: "bg-white border-emerald-200",
    warn: "bg-white border-amber-200",
    danger: "bg-white border-red-200",
  };
  const colors = {
    default: "text-slate-900",
    good: "text-emerald-700",
    warn: "text-amber-700",
    danger: "text-red-700",
  };
  return (
    <div className={`border p-4 rounded-sm ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
        {Icon && <Icon size={16} className={colors[tone]} weight="regular" />}
      </div>
      <div className={`text-[28px] font-bold mt-2 font-mono-num ${colors[tone]}`}>{value}</div>
    </div>
  );
}

export default function WarrantyTracker() {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/service/warranty-summary"),
      api.get("/projects"),
    ]);
    setData(a.data); setProjects(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  if (!data) {
    return (
      <>
        <PageHeader title="Warranty Tracker" subtitle="Loading…" module="Service · SV" />
        <div className="p-8 text-slate-500">Loading…</div>
      </>
    );
  }

  return (
    <div data-testid="warranty-page">
      <PageHeader
        title="Warranty Tracker"
        subtitle={`${data.total} equipment tracked · expiring in next 60 days highlighted`}
        module="Service · SV"
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="warranty-summary-cards">
          <Card label="Active" value={data.counts.active} tone="good" icon={ShieldCheck} />
          <Card label="Expiring soon" value={data.counts.expiring} tone="warn" icon={ShieldWarning} />
          <Card label="Expired" value={data.counts.expired} tone="danger" icon={ShieldWarning} />
          <Card label="Unknown" value={data.counts.unknown} tone="default" icon={Shield} />
        </div>

        <div>
          <div className="text-[11.5px] font-semibold uppercase tracking-[0.15em] text-slate-700 mb-2">
            Expiring within 60 days
          </div>
          {data.expiring_within_60_days.length === 0 ? (
            <EmptyState title="No warranties expiring in the next 60 days" description="Your fleet is in the clear for now." />
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="warranty-expiring-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="label-caps">Tag</TableHead>
                    <TableHead className="label-caps">Name</TableHead>
                    <TableHead className="label-caps">Project</TableHead>
                    <TableHead className="label-caps">Expiry Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.expiring_within_60_days.map((r) => (
                    <TableRow key={r.id} data-testid={`warranty-row-${r.id}`}>
                      <TableCell className="font-mono-num font-semibold text-[#EA580C]">{r.tag_no || "—"}</TableCell>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-slate-600 text-xs">{projMap[r.project_id] || "—"}</TableCell>
                      <TableCell className="font-mono-num text-amber-700 font-semibold">{fmtDate(r.warranty_expiry)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
